import React, { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import Utils from './utils';
import training from '../utils/training';
import '../Component/buttons/buttons.css';

// 添加旋转动画样式
const rotateStyle = `
@keyframes rotate {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
`;

// 创建样式标签并添加到文档头部
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = rotateStyle;
    if (!document.head.querySelector('style[data-calibration-rotate]')) {
        styleSheet.setAttribute('data-calibration-rotate', 'true');
        document.head.appendChild(styleSheet);
    }
}

// 全局校准对象
const CalibrationModel = {
    isCalibrated: false,
    calibrationData: [],

    // 存储校准结果
    setCalibrationData(data) {
        this.calibrationData = data;
        this.isCalibrated = data.length === pointArr.length;
        console.log('校准数据已保存:', this.calibrationData);
        console.log('校准状态:', this.isCalibrated ? '已完成' : '未完成');
    },

    // 获取校准状态
    getCalibrationStatus() {
        return {
            isCalibrated: this.isCalibrated,
            dataCount: this.calibrationData.length,
            totalPoints: pointArr.length
        };
    },

    // 将眼部原始数据转换为屏幕坐标
    convertToScreenCoordinates(eyeRawData) {
        if (!this.isCalibrated || !eyeRawData || eyeRawData.length !== 2) {
            console.warn('校准未完成或眼部数据无效');
            return null;
        }

        const [currentYaw, currentPitch] = eyeRawData;

        // 定义6个三角形区域，每个区域由三个校准点组成
        // pointArr索引: 0=[50,50], 1=[10,10], 2=[50,90], 3=[90,10], 4=[90,90], 5=[50,10], 6=[10,90]
        const triangles = [
            [0, 1, 5], // 中心-左上-上中 (上左区域)
            [0, 5, 3], // 中心-上中-右上 (上右区域)
            [0, 3, 4], // 中心-右上-右下 (右上区域)
            [0, 4, 2], // 中心-右下-下中 (右下区域)
            [0, 2, 6], // 中心-下中-左下 (下左区域)
            [0, 6, 1], // 中心-左下-左上 (左上区域)
        ];

        // 找到eyeRawData所属的三角形区域
        const targetTriangle = this.findTriangleRegion(currentYaw, currentPitch, triangles);

        if (targetTriangle === null) {
            // 如果找不到合适的三角形，使用最近邻插值作为后备方案
            console.warn('无法确定所属区域，使用最近邻插值');
            return this.nearestNeighborInterpolation(currentYaw, currentPitch);
        }

        // 使用重心坐标插值计算屏幕位置
        return this.barycentricInterpolation(currentYaw, currentPitch, targetTriangle);
    },

    // 找到eyeRawData所属的三角形区域
    findTriangleRegion(yaw, pitch, triangles) {
        for (const triangle of triangles) {
            if (this.isPointInTriangle(yaw, pitch, triangle)) {
                return triangle;
            }
        }

        // 如果不在任何三角形内，找到距离最近的三角形
        let minDistance = Infinity;
        let closestTriangle = null;

        for (const triangle of triangles) {
            const distance = this.distanceToTriangle(yaw, pitch, triangle);
            if (distance < minDistance) {
                minDistance = distance;
                closestTriangle = triangle;
            }
        }

        return closestTriangle;
    },

    // 判断点是否在三角形内 (使用重心坐标)
    isPointInTriangle(yaw, pitch, triangleIndices) {
        const [i1, i2, i3] = triangleIndices;
        const p1 = { x: this.calibrationData[i1].averageYaw, y: this.calibrationData[i1].averagePitch };
        const p2 = { x: this.calibrationData[i2].averageYaw, y: this.calibrationData[i2].averagePitch };
        const p3 = { x: this.calibrationData[i3].averageYaw, y: this.calibrationData[i3].averagePitch };
        const p = { x: yaw, y: pitch };

        // 计算重心坐标
        const denom = (p2.y - p3.y) * (p1.x - p3.x) + (p3.x - p2.x) * (p1.y - p3.y);
        if (Math.abs(denom) < 0.0001) return false; // 退化三角形

        const a = ((p2.y - p3.y) * (p.x - p3.x) + (p3.x - p2.x) * (p.y - p3.y)) / denom;
        const b = ((p3.y - p1.y) * (p.x - p3.x) + (p1.x - p3.x) * (p.y - p3.y)) / denom;
        const c = 1 - a - b;

        // 点在三角形内当且仅当所有重心坐标都非负 (允许小的误差)
        const epsilon = -0.01; // 允许小的边界误差
        return a >= epsilon && b >= epsilon && c >= epsilon;
    },

    // 计算点到三角形的距离
    distanceToTriangle(yaw, pitch, triangleIndices) {
        const [i1, i2, i3] = triangleIndices;
        const p1 = this.calibrationData[i1];
        const p2 = this.calibrationData[i2];
        const p3 = this.calibrationData[i3];

        // 计算到三个顶点的距离的平均值
        const d1 = Math.sqrt(Math.pow(yaw - p1.averageYaw, 2) + Math.pow(pitch - p1.averagePitch, 2));
        const d2 = Math.sqrt(Math.pow(yaw - p2.averageYaw, 2) + Math.pow(pitch - p2.averagePitch, 2));
        const d3 = Math.sqrt(Math.pow(yaw - p3.averageYaw, 2) + Math.pow(pitch - p3.averagePitch, 2));

        return (d1 + d2 + d3) / 3;
    },

    // 重心坐标插值
    barycentricInterpolation(yaw, pitch, triangleIndices) {
        const [i1, i2, i3] = triangleIndices;
        const p1 = {
            yaw: this.calibrationData[i1].averageYaw,
            pitch: this.calibrationData[i1].averagePitch,
            screen: this.calibrationData[i1].point
        };
        const p2 = {
            yaw: this.calibrationData[i2].averageYaw,
            pitch: this.calibrationData[i2].averagePitch,
            screen: this.calibrationData[i2].point
        };
        const p3 = {
            yaw: this.calibrationData[i3].averageYaw,
            pitch: this.calibrationData[i3].averagePitch,
            screen: this.calibrationData[i3].point
        };

        // 计算重心坐标
        const denom = (p2.pitch - p3.pitch) * (p1.yaw - p3.yaw) + (p3.yaw - p2.yaw) * (p1.pitch - p3.pitch);
        if (Math.abs(denom) < 0.0001) {
            // 退化三角形，使用最近邻
            return this.nearestNeighborInterpolation(yaw, pitch);
        }

        const a = ((p2.pitch - p3.pitch) * (yaw - p3.yaw) + (p3.yaw - p2.yaw) * (pitch - p3.pitch)) / denom;
        const b = ((p3.pitch - p1.pitch) * (yaw - p3.yaw) + (p1.yaw - p3.yaw) * (pitch - p3.pitch)) / denom;
        const c = 1 - a - b;

        // 使用重心坐标插值屏幕坐标
        const screenX = a * p1.screen[0] + b * p2.screen[0] + c * p3.screen[0];
        const screenY = a * p1.screen[1] + b * p2.screen[1] + c * p3.screen[1];

        // 限制坐标在合理范围内
        const clampedX = Math.max(0, Math.min(100, screenX));
        const clampedY = Math.max(0, Math.min(100, screenY));

        console.log(`使用三角形 [${i1},${i2},${i3}] 插值: 重心坐标 [${a.toFixed(3)},${b.toFixed(3)},${c.toFixed(3)}] -> 屏幕坐标 [${clampedX.toFixed(2)},${clampedY.toFixed(2)}]`);

        return {
            x: clampedX,
            y: clampedY
        };
    },

    // 最近邻插值 (后备方案)
    nearestNeighborInterpolation(yaw, pitch) {
        let minDistance = Infinity;
        let nearestPoint = null;

        for (const calibPoint of this.calibrationData) {
            const distance = Math.sqrt(
                Math.pow(yaw - calibPoint.averageYaw, 2) +
                Math.pow(pitch - calibPoint.averagePitch, 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                nearestPoint = calibPoint;
            }
        }

        return nearestPoint ? {
            x: nearestPoint.point[0],
            y: nearestPoint.point[1]
        } : null;
    },

    // 获取校准数据
    getCalibrationData() {
        return this.calibrationData;
    },

    // 清除校准数据
    clearCalibration() {
        this.calibrationData = [];
        this.isCalibrated = false;
        console.log('校准数据已清除');
    }
};

// 将校准对象暴露到全局作用域（可选）
if (typeof window !== 'undefined') {
    window.CalibrationModel = CalibrationModel;
}

const pointArr = [
    [50, 50],
    [10, 10],
    [50, 90],
    [90, 10],
    [90, 90],
    [50, 10],
    [10, 90],
]


let Calibration = (props, ref) => {
    const [message, setMessage] = useState('')
    const [targetDisplay, setTargetDisplay] = useState('none')
    const [pos, setPos] = useState([50, 50])
    const stateRef = useRef()
    const [isStart, setStart] = useState(false)
    const eyeRawDataRef = useRef(null)
    const [currentPointIndex, setCurrentPointIndex] = useState(0)
    const [calibrationResults, setCalibrationResults] = useState([])
    const dataBuffer = useRef([])
    const validCount = useRef(0)
    const averageValues = useRef({ yaw: 0, pitch: 0 })
    const [isValidating, setIsValidating] = useState(false)
    const [currentPointCompleted, setCurrentPointCompleted] = useState(false)

    function startCalibration() {
        if (props.btMsg === 'Calibration') {
            props.stateChange()
            console.log('start Calibration');
            stateRef.current = true
            setStart(true)
            setTargetDisplay('block')
            setCurrentPointIndex(0)
            setCalibrationResults([])
            dataBuffer.current = []
            validCount.current = 0
            averageValues.current = { yaw: 0, pitch: 0 }
            setIsValidating(false)
            setCurrentPointCompleted(false)
            setPos([50, 50])
            // setMessage('請注視藍色圓點')
            setTimeout(() => {
                startPointCalibration(0);
            }, 1000);
        }
    }

    // 完成校准
    const finishCalibration = useCallback((finalResults) => {
        setTargetDisplay('none');
        console.log('校准结果:', finalResults);

        // 将校准结果保存到全局对象
        CalibrationModel.setCalibrationData(finalResults);

    }, []);

    // 开始特定点的校准
    const startPointCalibration = useCallback((pointIndex, currentResults = []) => {
        if (pointIndex >= pointArr.length) {
            // 所有点校准完成，传递最新的校准结果
            finishCalibration(currentResults);
            return;
        }

        setCurrentPointIndex(pointIndex);
        setPos(pointArr[pointIndex]);
        // setMessage(`校准点 ${pointIndex + 1}/${pointArr.length} - 請注視藍色圓點`);

        // 重置校准状态
        dataBuffer.current = [];
        validCount.current = 0;
        averageValues.current = { yaw: 0, pitch: 0 };
        setIsValidating(false);
        setCurrentPointCompleted(false);
    }, [finishCalibration]);

    // 计算平均值
    function calculateAverage(data) {
        if (data.length === 0) return { yaw: 0, pitch: 0 };

        const sum = data.reduce((acc, curr) => ({
            yaw: acc.yaw + curr[0],
            pitch: acc.pitch + curr[1]
        }), { yaw: 0, pitch: 0 });

        return {
            yaw: sum.yaw / data.length,
            pitch: sum.pitch / data.length
        };
    }

    // 检查数据是否有效
    function isDataValid(newData, average) {
        console.log('检查数据有效性:', newData, average);
        const yawDiff = Math.abs(newData[0] - average.yaw);
        const pitchDiff = Math.abs(newData[1] - average.pitch);
        return yawDiff < 0.1 && pitchDiff < 0.1;
    }

    // 处理眼部数据
    const processEyeData = useCallback((newData) => {
        // 如果当前点已完成校准，则不再处理数据
        if (currentPointCompleted) {
            return;
        }
        const eyeCount=15
        if (dataBuffer.current.length < eyeCount) {
            dataBuffer.current.push(newData);
            if (dataBuffer.current.length === eyeCount) {
                averageValues.current = calculateAverage(dataBuffer.current);
                console.log(`点 ${currentPointIndex + 1} 初始平均值:`, averageValues.current);
            }
        } else {
            averageValues.current = calculateAverage(dataBuffer.current);
            dataBuffer.current.shift();
            dataBuffer.current.push(newData);
            // 第31个数据开始，检查有效性
            if (isDataValid(newData, averageValues.current)) {
                // 有效数据：移除第一个，添加新数据到末尾
                // 增加有效计数并开始验证动画
                validCount.current++;
                setIsValidating(true);
                console.log(`点 ${currentPointIndex + 1} 有效数据计数: ${validCount.current}/eyeCount`);

                // 检查是否连续eyeCount个有效数据
                if (validCount.current >= eyeCount) {
                    // 保存此点的校准结果
                    const result = {
                        point: pointArr[currentPointIndex],
                        averageYaw: averageValues.current.yaw,
                        averagePitch: averageValues.current.pitch
                    };

                    // 更新校准结果并立即获取最新结果
                    const updatedResults = [...calibrationResults, result];
                    setCalibrationResults(updatedResults);
                    console.log(`点 ${currentPointIndex + 1} 校准完成:`, result);

                    // 停止处理当前点的数据，进入下一个点的校准，传递最新结果
                    setIsValidating(false);
                    setCurrentPointCompleted(true);
                    setTimeout(() => {
                        startPointCalibration(currentPointIndex + 1, updatedResults);
                    }, 500);
                    return; // 重要：立即返回，避免继续处理数据
                }
            } else {
                // 无效数据：重置有效计数和验证状态
                validCount.current = 0;
                setIsValidating(false);
                console.log(`点 ${currentPointIndex + 1} 数据无效，重置计数`);
            }
        }
    }, [currentPointIndex, startPointCalibration, currentPointCompleted, calibrationResults]);

    useEffect(() => {
        eyeRawDataRef.current = props.eyeRawData;

        // 只有在校准过程中才处理数据
        if (isStart && targetDisplay === 'block' && props.eyeRawData && props.eyeRawData.length === 2) {
            const [yaw, pitch] = props.eyeRawData;

            // 检查数据是否在有效范围内 (0-1)
            if (yaw >= 0 && yaw <= 1 && pitch >= 0 && pitch <= 1) {
                processEyeData([yaw, pitch]);
            }
        }
    }, [props.eyeRawData, isStart, targetDisplay, processEyeData]);

    return (
        <div>
            <div className='description' style={{ display: isStart ? 'none' : 'block' }}>
                <div>
                    <div style={{
                        left: '20%',
                        top: '50%',
                        width: '60%',
                        position: 'absolute',
                    }}>
                        <p style={{
                            fontSize: '28px'
                        }}>使用指引與注意事項</p>
                        <p align="left" style={{
                            fontSize: '28px'
                        }}>
                            ★在校準的過程中：<br />
                            &nbsp; 1.請盡量保持頭部（眼部）的高度不變；<br />
                            &nbsp; 2.請盡量保持面部與屏幕相對距離不變；<br />
                            &nbsp; 3.頭部與眼部可按照平時自然正常的姿態進行校準（允許頭部轉動）。<br />
                        </p>
                    </div>
                    <button className={'bt medium ' + props.btColor}
                        style={{
                            bottom: '6%',
                            left: '50%',
                            width: '10%',
                            transform: 'translate(-50%,0)',
                            position: 'absolute'
                        }}
                        onClick={startCalibration}>{props.btMsg}</button>

                </div>
            </div>
            <div className='calibration' style={{
                display: isStart ? 'block' : 'none',
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'black',
                zIndex: 1000
            }}>
                {/* 蓝色圆点 */}
                <div style={{
                    display: targetDisplay,
                    position: 'absolute',
                    width: isValidating ? '120px' : '100px',
                    height: isValidating ? '80px' : '100px',
                    backgroundColor: 'blue',
                    borderRadius: '50%',
                    left: pos[0] + '%',
                    top: pos[1] + '%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1001,
                    animation: isValidating ? 'rotate 1s linear infinite' : 'none',
                    transition: 'width 0.3s ease, height 0.3s ease'
                }} />

                {/* 消息显示 */}
                <div style={{
                    position: 'absolute',
                    top: "10%",
                    left: "50%",
                    transform: 'translateX(-50%)',
                    color: 'white',
                    textAlign: 'center',
                    zIndex: 1001
                }}>
                    <label style={{
                        fontSize: '28px'
                    }}>{message}</label>
                </div>
            </div>
        </div>
    )
}
Calibration = forwardRef(Calibration)

// 导出校准组件和校准模型
export default Calibration;
export { CalibrationModel };